import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Receipt, Trash2, Wifi, WifiOff, CreditCard, Banknote, Smartphone, X, LogOut, ArrowLeftRight, History, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import TopBar from "./TopBar";
import { genClientId, pushQueue, flushQueue, listQueue } from "../lib/offlineQueue";
import { getBranding, subscribeBranding } from "../lib/branding";
import { fmtEUR } from "../lib/format";
import { getAuth, getTeam, clearAuth } from "../lib/auth";
import OrderDetailModal from "../components/OrderDetailModal";
import { getDeviceId } from "../lib/device";
import { attachSlotAutoRelease } from "../lib/slotRelease";
import { switchRole } from "../lib/roleSwitch";

const PAYMENT_METHODS = [
  { id: "contant", label: "Contant", icon: Banknote, color: "#06D6A0" },
  { id: "sumup", label: "SumUp (Tap of Solo)", icon: Smartphone, color: "#0070BA" },
];

export default function Kassa() {
  const navigate = useNavigate();
  const team = getTeam();
  const [teams, setTeams] = useState([]); // gewone publieke lijst (voor terminal-info per team)
  const activeTeam = useMemo(() => teams.find((t) => t.id === team?.id) || team, [teams, team]);

  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState([]);
  const [notitie, setNotitie] = useState("");
  const [bonPrinten, setBonPrinten] = useState(false);
  const [paying, setPaying] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [queueSize, setQueueSize] = useState(listQueue().length);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sumupCfg, setSumupCfg] = useState(null);
  const [brand, setBrand] = useState(getBranding());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [cashOpen, setCashOpen] = useState(false);
  const bonnenUit = !!brand?.bonnen_uit;

  useEffect(() => {
    const unsub = subscribeBranding(setBrand);
    return unsub;
  }, []);

  // Auto-release slot bij tab close (alleen voor team-rol, niet admin)
  useEffect(() => {
    const auth = getAuth();
    return attachSlotAutoRelease(auth?.role);
  }, []);

  useEffect(() => {
    let alive = true;
    api.get("/payments/sumup/config")
      .then((r) => { if (alive) setSumupCfg(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      flushQueue().then((r) => {
        setQueueSize(listQueue().length);
        if (r.synced > 0) toast.success(`${r.synced} offline orders gesynchroniseerd`);
      });
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Polled team-orders voor geschiedenis-sheet
  const fetchHist = async () => {
    if (!team) return;
    try {
      const { data } = await api.get("/orders?limit=200");
      setAllOrders(data.filter((o) => o.team_id === team.id && !o.kassa_archived));
    } catch {}
  };
  useEffect(() => {
    if (!team) return;
    fetchHist();
    const id = setInterval(fetchHist, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.id]);

  const load = async () => {
    try {
      const [t, m] = await Promise.all([api.get("/teams"), api.get("/menu")]);
      setTeams(t.data);
      setMenu(m.data);
    } catch (e) {
      const cached = JSON.parse(localStorage.getItem("teller_cache") || "{}");
      if (cached.teams) setTeams(cached.teams);
      if (cached.menu) setMenu(cached.menu);
    }
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (menu.length && teams.length) {
      localStorage.setItem("teller_cache", JSON.stringify({ teams, menu }));
    }
  }, [menu, teams]);

  const categories = useMemo(() => {
    const set = new Set(menu.filter((m) => m.actief !== false).map((m) => m.categorie || "Algemeen"));
    return ["Alle", ...Array.from(set)];
  }, [menu]);
  const [activeCat, setActiveCat] = useState("Alle");

  const filteredMenu = useMemo(() => {
    return menu
      .filter((m) => m.actief !== false && m.zichtbaar !== false)
      .filter((m) => activeCat === "Alle" || (m.categorie || "Algemeen") === activeCat)
      .slice()
      .sort((a, b) => (a.naam || "").localeCompare(b.naam || "", "nl"));
  }, [menu, activeCat]);

  const addToCart = (item) => {
    if (item.uitverkocht) {
      toast.warning(`${item.naam} is uitverkocht`);
      return;
    }
    if (!item.prijs || item.prijs <= 0) {
      toast.warning(`${item.naam}: prijs nog niet ingesteld`);
      return;
    }
    setCart((c) => {
      const i = c.findIndex((x) => x.menu_item_id === item.id);
      if (i >= 0) {
        const copy = [...c];
        copy[i] = { ...copy[i], aantal: copy[i].aantal + 1 };
        return copy;
      }
      return [
        ...c,
        {
          menu_item_id: item.id,
          naam: item.naam,
          prijs: item.prijs,
          aantal: 1,
          voorbereidingstype: item.voorbereidingstype || "",
          productnotitie: item.productnotitie || "",
        },
      ];
    });
  };

  const inc = (i) =>
    setCart((c) => c.map((x, idx) => (idx === i ? { ...x, aantal: x.aantal + 1 } : x)));
  const dec = (i) =>
    setCart((c) =>
      c.flatMap((x, idx) => {
        if (idx !== i) return [x];
        const next = x.aantal - 1;
        return next <= 0 ? [] : [{ ...x, aantal: next }];
      })
    );
  const remove = (i) => setCart((c) => c.filter((_, idx) => idx !== i));
  const clear = () => {
    setCart([]);
    setNotitie("");
    setBonPrinten(false);
  };

  const totaal = useMemo(() => cart.reduce((s, x) => s + x.prijs * x.aantal, 0), [cart]);

  const checkout = async (method, opts = {}) => {
    if (!activeTeam) return toast.error("Geen team gekoppeld — log opnieuw in");
    if (!cart.length) return toast.error("Winkelmandje is leeg");
    // Contant → eerst wisselgeld-berekenaar tonen
    if (method === "contant" && !opts.skipCashDialog) {
      setSheetOpen(false);
      setCashOpen(true);
      return;
    }
    setPaying(true);
    try {
      if (method === "sumup-solo") {
        if (!online) {
          toast.error("Solo terminal vereist internet");
          setPaying(false);
          return;
        }
        const body = {
          order_payload: {
            team_id: activeTeam.id,
            items: cart,
            betaalmethode: "sumup",
            notitie,
            bon_printen: bonPrinten,
          },
        };
        const { data } = await api.post("/payments/sumup/solo/start", body);
        toast.success(`Verzonden naar Solo terminal · ${data.ordernummer}`);
        sessionStorage.setItem(
          "teller_sumup_pending",
          JSON.stringify({
            order_id: data.order_id,
            ordernummer: data.ordernummer,
            totaal: data.totaal,
            flow: "solo",
          })
        );
        navigate(`/kassa/sumup-solo/${data.order_id}`);
        return;
      }

      if (method === "sumup") {
        if (!online) {
          toast.error("SumUp betaling vereist internet");
          setPaying(false);
          return;
        }
        const body = {
          order_payload: {
            team_id: activeTeam.id,
            items: cart,
            betaalmethode: "sumup",
            notitie,
            bon_printen: bonPrinten,
          },
          origin_url: window.location.origin,
        };
        const { data } = await api.post("/payments/sumup/appswitch/start", body);
        sessionStorage.setItem(
          "teller_sumup_pending",
          JSON.stringify({
            order_id: data.order_id,
            ordernummer: data.ordernummer,
            totaal: data.totaal,
            cart_snapshot: cart,
            bon_printen: bonPrinten,
          })
        );
        toast.info(`Open SumUp app voor ${data.ordernummer}…`);
        window.location.href = data.sumup_url;
        return;
      }

      if (method === "stripe") {
        if (!online) {
          toast.error("Online betaling vereist internet");
          setPaying(false);
          return;
        }
        const body = {
          order_payload: {
            team_id: activeTeam.id,
            items: cart,
            betaalmethode: "stripe",
            notitie,
            bon_printen: bonPrinten,
          },
          origin_url: window.location.origin,
        };
        const { data } = await api.post("/checkout/session", body);
        sessionStorage.setItem(
          "teller_pending_checkout",
          JSON.stringify({ session_id: data.session_id, amount: data.amount })
        );
        window.location.href = data.url;
        return;
      }

      const payload = {
        team_id: activeTeam.id,
        items: cart,
        betaalmethode: method,
        notitie,
        bon_printen: bonPrinten,
        client_order_id: genClientId(),
      };
      if (!online) {
        pushQueue(payload);
        setQueueSize(listQueue().length);
        toast.success(`Order opgeslagen (offline). Wordt later gesynchroniseerd.`);
        if (!bonnenUit && bonPrinten) window.print();
        clear();
        return;
      }
      const { data } = await api.post("/orders", payload);
      toast.success(`Order ${data.ordernummer} geplaatst!`, { duration: 2500 });
      if (!bonnenUit && (bonPrinten || activeTeam?.auto_print_bon)) printBon(data);
      setConfirmedOrder(data);
      clear();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon order niet plaatsen");
    } finally {
      setPaying(false);
    }
  };

  if (!team) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-center">
        <div>
          <div className="font-display text-3xl font-extrabold mb-2">Geen team gekoppeld</div>
          <div className="text-[var(--ink-soft)] mb-4">Log opnieuw in met je team-code.</div>
          <button onClick={() => navigate("/")} className="btn-press px-5 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-bold">
            Naar login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="kassa-screen">
      <TopBar
        title={`Kassa · ${activeTeam?.naam || team.naam}`}
        subtitle="Bestellingen aannemen"
        color={activeTeam?.kleur || team.kleur}
      >
        {activeTeam?.terminal_provider && (
          <span
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border-2 border-[var(--neutral)] text-xs font-bold text-[var(--ink)]"
            data-testid="active-terminal-label"
          >
            <CreditCard className="w-3.5 h-3.5" />
            {activeTeam.terminal_label || activeTeam.terminal_provider}
            {activeTeam.terminal_id && (
              <span className="text-[var(--ink-soft)] font-medium ml-1">#{activeTeam.terminal_id}</span>
            )}
          </span>
        )}
        <div className="flex items-center gap-2 text-sm">
          {online ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[var(--success)]/15 text-[var(--success)] font-semibold">
              <Wifi className="w-4 h-4" /> Online
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[var(--warning)]/30 text-[var(--ink)] font-semibold" data-testid="offline-indicator">
              <WifiOff className="w-4 h-4" /> Offline {queueSize > 0 && `· ${queueSize} wachtend`}
            </span>
          )}
        </div>
        <button
          onClick={async () => {
            try {
              await switchRole("voorbereider");
              toast.success("Gewisseld naar Voorbereider");
              navigate("/voorbereider");
            } catch (e) {
              toast.error(e?.response?.data?.detail || e.message || "Wisselen mislukt");
            }
          }}
          className="btn-press inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border-2 border-[var(--neutral)] text-sm font-bold text-[var(--ink)]"
          data-testid="kassa-switch-voorbereider"
          title="Wissel naar Voorbereider (zelfde team)"
        >
          <ArrowLeftRight className="w-4 h-4" /> Wissel naar Voorbereider
        </button>
        <button
          onClick={() => setHistoryOpen(true)}
          className="btn-press inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border-2 border-[var(--neutral)] text-sm font-bold text-[var(--ink)]"
          data-testid="kassa-history-btn"
        >
          <History className="w-4 h-4" /> Geschiedenis <span className="text-[var(--ink-soft)]">({allOrders.length})</span>
        </button>
        <button
          onClick={async () => {
            const a = getAuth();
            if (a?.role === "admin") {
              navigate("/admin");
              return;
            }
            try {
              await api.post("/auth/logout", { code: "", device_id: getDeviceId() });
            } catch {}
            clearAuth();
            navigate("/");
          }}
          className="btn-press inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--ink)] text-white text-sm font-bold"
          data-testid="kassa-logout-btn"
        >
          <LogOut className="w-4 h-4" /> Uitloggen
        </button>
      </TopBar>

      <div className="max-w-[1600px] w-full mx-auto px-4 md:px-6 py-4 flex-1 flex gap-6">
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {/* Team badge */}
          <div
            className="rounded-2xl px-4 py-3 text-white flex items-center gap-3"
            style={{ background: activeTeam?.kleur || team.kleur }}
            data-testid="kassa-team-badge"
          >
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center font-display font-extrabold text-2xl">
              {team.letter}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] opacity-90 font-bold">
                Ingelogd als
              </div>
              <div className="font-display font-extrabold text-xl truncate">
                {activeTeam?.naam || team.naam} · Kassa
              </div>
            </div>
          </div>

          {categories.length > 1 && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="categories">
              {categories.map((c) => {
                const active = c === activeCat;
                return (
                  <button
                    key={c}
                    onClick={() => setActiveCat(c)}
                    className={`btn-press px-4 py-2 rounded-full text-sm font-semibold border-2 whitespace-nowrap ${
                      active
                        ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                        : "bg-white text-[var(--ink)] border-[#ecebe6]"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="product-grid">
            {filteredMenu.length === 0 && (
              <div className="col-span-full tile p-10 text-center text-[var(--ink-soft)]" data-testid="menu-empty-kassa">
                Nog geen producten. Vraag de admin om producten toe te voegen.
              </div>
            )}
            {filteredMenu.map((item) => {
              const noPrice = !item.prijs || item.prijs <= 0;
              const disabled = item.uitverkocht || noPrice;
              return (
                <button
                  key={item.id}
                  onClick={() => addToCart(item)}
                  disabled={disabled}
                  className="btn-press tile p-4 flex flex-col items-start justify-between min-h-[140px] text-left disabled:opacity-50 relative"
                  style={{ borderColor: disabled ? "#ecebe6" : (activeTeam?.kleur || team.kleur) + "55" }}
                  data-testid={`product-btn-${item.id}`}
                  title={item.productnotitie || undefined}
                >
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center font-display text-2xl text-white"
                    style={{ background: activeTeam?.kleur || team.kleur }}
                  >
                    {item.emoji || item.naam.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="mt-2 w-full">
                    <div className="font-display font-bold text-lg leading-tight">{item.naam}</div>
                    <div className="text-sm font-semibold text-[var(--ink-soft)]">
                      {noPrice ? "Prijs n.v.t." : fmtEUR(item.prijs)}
                    </div>
                    {item.uitverkocht && (
                      <div className="text-[10px] uppercase tracking-widest text-[var(--danger)] mt-1">Uitverkocht</div>
                    )}
                    {item.productnotitie && !item.uitverkocht && (
                      <div className="text-[10px] text-[var(--ink-soft)] italic mt-1 line-clamp-1">📝 {item.productnotitie}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <aside className="w-[380px] hidden md:flex flex-col tile p-5 sticky top-[88px] self-start max-h-[calc(100vh-110px)]" data-testid="cart-sidebar">
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="w-5 h-5" />
            <h2 className="font-display text-2xl font-bold">Winkelmandje</h2>
          </div>
          <div className="flex-1 overflow-y-auto -mx-2 px-2" data-testid="cart-items">
            {cart.length === 0 ? (
              <div className="text-center text-[var(--ink-soft)] py-10">Voeg producten toe</div>
            ) : (
              cart.map((x, i) => (
                <div key={x.menu_item_id} className="flex items-center gap-2 py-2 border-b border-[#ecebe6] last:border-0" data-testid={`cart-line-${x.menu_item_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{x.naam}</div>
                    <div className="text-xs text-[var(--ink-soft)]">{fmtEUR(x.prijs)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => dec(i)} className="btn-press w-8 h-8 rounded-full bg-white border-2 border-[#ecebe6] flex items-center justify-center" data-testid={`cart-dec-${x.menu_item_id}`}>
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-6 text-center font-display font-bold">{x.aantal}</span>
                    <button onClick={() => inc(i)} className="btn-press w-8 h-8 rounded-full bg-white border-2 border-[#ecebe6] flex items-center justify-center" data-testid={`cart-inc-${x.menu_item_id}`}>
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <button onClick={() => remove(i)} className="btn-press p-1.5 rounded-full bg-[var(--danger)] text-white" data-testid={`cart-remove-${x.menu_item_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="mt-3 space-y-2">
            <textarea
              value={notitie}
              onChange={(e) => setNotitie(e.target.value)}
              placeholder="Notitie (allergie, extra saus…)"
              className="input min-h-[60px] resize-none"
              data-testid="cart-notitie"
            />
            {!bonnenUit && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={bonPrinten}
                  onChange={(e) => setBonPrinten(e.target.checked)}
                  className="w-5 h-5"
                  data-testid="cart-bon-print"
                />
                Bon printen
              </label>
            )}
          </div>

          <div className="mt-4 pt-3 border-t-2 border-dashed border-[#ecebe6]">
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-sm uppercase tracking-widest text-[var(--ink-soft)]">Totaal</span>
              <span className="font-display text-4xl font-bold" data-testid="cart-totaal">{fmtEUR(totaal)}</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {PAYMENT_METHODS.filter((pm) => {
                if (pm.id === "sumup") return sumupCfg?.appswitch_available !== false;
                return true;
              }).map((pm) => {
                const Icon = pm.icon;
                return (
                  <button
                    key={pm.id}
                    onClick={() => checkout(pm.id)}
                    disabled={paying || cart.length === 0}
                    className="btn-press inline-flex items-center justify-center gap-2 px-3 py-3 rounded-2xl text-white font-semibold text-sm disabled:opacity-50"
                    style={{ background: pm.color }}
                    data-testid={`checkout-${pm.id}`}
                  >
                    <Icon className="w-4 h-4" /> {pm.label}
                  </button>
                );
              })}
              {sumupCfg?.solo_available && activeTeam?.terminal_id && (
                <button
                  onClick={() => checkout("sumup-solo")}
                  disabled={paying || cart.length === 0}
                  className="btn-press inline-flex items-center justify-center gap-2 px-3 py-3 rounded-2xl text-white font-semibold text-sm disabled:opacity-50 col-span-2"
                  style={{ background: "#003D7A" }}
                  data-testid="checkout-sumup-solo"
                >
                  <Smartphone className="w-4 h-4" /> SumUp Solo Terminal
                </button>
              )}
            </div>
            <button
              onClick={clear}
              className="btn-press w-full mt-2 px-3 py-2 rounded-2xl bg-white border-2 border-[#ecebe6] text-sm font-semibold"
              data-testid="cart-clear"
            >
              Mandje leegmaken
            </button>
          </div>
        </aside>
      </div>

      <button
        onClick={() => cart.length > 0 && setSheetOpen(true)}
        disabled={!cart.length}
        className="md:hidden sticky bottom-0 left-0 right-0 z-30 flex items-stretch border-t-2 border-[#ecebe6] disabled:opacity-50 w-full"
        data-testid="mobile-cart-bar"
      >
        <div className="flex-1 bg-white px-4 py-3 text-left">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold">
            {cart.length} {cart.length === 1 ? "item" : "items"} · {activeTeam?.naam || team.naam}
          </div>
          <div className="font-display text-3xl font-extrabold" data-testid="mobile-cart-total">
            {fmtEUR(totaal)}
          </div>
        </div>
        <div
          className="px-6 flex items-center justify-center text-white font-display font-extrabold text-lg"
          style={{ background: activeTeam?.kleur || team.kleur }}
        >
          Afrekenen →
        </div>
      </button>

      {sheetOpen && (
        <MobileCheckoutSheet
          cart={cart}
          totaal={totaal}
          notitie={notitie}
          setNotitie={setNotitie}
          bonPrinten={bonPrinten}
          setBonPrinten={setBonPrinten}
          bonnenUit={bonnenUit}
          paying={paying}
          activeTeam={activeTeam || team}
          sumupCfg={sumupCfg}
          inc={inc}
          dec={dec}
          remove={remove}
          clear={clear}
          onClose={() => setSheetOpen(false)}
          onCheckout={(method) => {
            setSheetOpen(false);
            checkout(method);
          }}
        />
      )}

      {confirmedOrder && (
        <OrderConfirmModal
          order={confirmedOrder}
          onClose={() => setConfirmedOrder(null)}
          onReshow={() => {
            const o = confirmedOrder;
            setConfirmedOrder(null);
            setTimeout(() => setConfirmedOrder(o), 50);
          }}
        />
      )}

      {historyOpen && (
        <KassaHistorySheet
          orders={allOrders}
          teamId={activeTeam?.id || team.id}
          teamKleur={activeTeam?.kleur || team.kleur}
          onClose={() => setHistoryOpen(false)}
          onChanged={fetchHist}
        />
      )}

      {cashOpen && (
        <CashChangeModal
          totaal={totaal}
          onCancel={() => setCashOpen(false)}
          onConfirm={() => {
            setCashOpen(false);
            checkout("contant", { skipCashDialog: true });
          }}
        />
      )}
    </div>
  );
}

function KassaHistorySheet({ orders, teamId, teamKleur, onClose, onChanged }) {
  const sorted = [...orders].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [selected, setSelected] = useState(null);

  const removeOne = async (orderId, ordernummer) => {
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/archive-kassa`);
      toast.success(`Order ${ordernummer} uit geschiedenis gehaald`);
      onChanged?.();
    } catch {
      toast.error("Kon order niet verwijderen");
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders/archive-kassa/all", { team_id: teamId });
      toast.success(`${data.count || 0} order(s) gewist uit geschiedenis`);
      setConfirmClear(false);
      onChanged?.();
    } catch {
      toast.error("Kon geschiedenis niet wissen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch md:items-center justify-end md:justify-center p-0 md:p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="kassa-history-sheet"
    >
      <div className="bg-white w-full md:max-w-2xl md:rounded-3xl flex flex-col max-h-[90vh] md:max-h-[80vh] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b-2 border-[#ecebe6]" style={{ borderTopColor: teamKleur }}>
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold">
              Kassa · Geschiedenis vandaag
            </div>
            <div className="font-display text-2xl font-extrabold inline-flex items-center gap-2">
              <History className="w-5 h-5" /> Bestellingen ({sorted.length})
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sorted.length > 0 && (
              <button
                onClick={() => setConfirmClear(true)}
                disabled={busy}
                className="btn-press inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[var(--danger)] text-white text-xs font-bold disabled:opacity-50"
                data-testid="kassa-history-clear-all"
              >
                <Trash2 className="w-4 h-4" /> Wis alles
              </button>
            )}
            <button
              onClick={onClose}
              className="btn-press w-11 h-11 rounded-full bg-[var(--neutral)] flex items-center justify-center"
              data-testid="kassa-history-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {sorted.length === 0 ? (
            <div className="text-center text-[var(--ink-soft)] py-12" data-testid="kassa-history-empty">
              Nog geen bestellingen vandaag.
            </div>
          ) : (
            <div className="space-y-2">
              {sorted.map((o) => {
                const statusColor = o.status === "geannuleerd" ? "#999" : o.betaal_status === "wachten" ? "#FF8A00" : "#0E8F2A";
                return (
                  <div
                    key={o.id}
                    onClick={() => setSelected(o)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(o); } }}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-2xl bg-[var(--neutral)] hover:bg-[#e8e6df] transition-colors cursor-pointer btn-press"
                    data-testid={`kassa-history-row-${o.ordernummer}`}
                    aria-label={`Bekijk order ${o.ordernummer}`}
                  >
                    <div
                      className="font-display font-extrabold text-2xl px-3 py-1 rounded-xl text-white leading-none"
                      style={{ background: o.team_kleur }}
                    >
                      {o.ordernummer}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">
                        {o.items.map((i) => `${i.aantal}× ${i.naam}`).join(", ")}
                      </div>
                      <div className="text-xs text-[var(--ink-soft)] inline-flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {new Date(o.created_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
                        · {fmtEUR(o.totaal)}
                        · {o.betaalmethode}
                      </div>
                    </div>
                    <span
                      className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded text-white"
                      style={{ background: statusColor }}
                    >
                      {o.status === "geannuleerd" ? "Geann." : o.betaal_status === "wachten" ? "Wacht" : o.status}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeOne(o.id, o.ordernummer); }}
                      disabled={busy}
                      className="btn-press w-9 h-9 rounded-full bg-white border-2 border-[var(--danger)] text-[var(--danger)] flex items-center justify-center disabled:opacity-40"
                      title="Verwijder uit geschiedenis"
                      data-testid={`kassa-history-remove-${o.ordernummer}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selected && <OrderDetailModal order={selected} onClose={() => setSelected(null)} />}

        {confirmClear && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.6)" }}
            data-testid="kassa-history-clear-confirm"
            onClick={(e) => e.target === e.currentTarget && setConfirmClear(false)}
          >
            <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl border-4 border-[var(--danger)]">
              <div className="font-display text-xl font-extrabold text-[var(--danger)] mb-2">
                Hele geschiedenis wissen?
              </div>
              <p className="text-sm text-[var(--ink-soft)] mb-4">
                Alle <strong>{sorted.length}</strong> bestellingen verdwijnen uit deze
                Kassa-geschiedenis. Ze blijven wel zichtbaar in admin-rapporten en de
                Voorbereider-historie.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmClear(false)}
                  className="btn-press flex-1 px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
                  data-testid="kassa-history-clear-cancel"
                >
                  Annuleer
                </button>
                <button
                  onClick={clearAll}
                  disabled={busy}
                  className="btn-press flex-1 px-4 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold disabled:opacity-50"
                  data-testid="kassa-history-clear-confirm-btn"
                >
                  {busy ? "Bezig…" : "Wis alles"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileCheckoutSheet({ cart, totaal, notitie, setNotitie, bonPrinten, setBonPrinten, bonnenUit, paying, activeTeam, sumupCfg, inc, dec, remove, clear, onClose, onCheckout }) {
  const methods = PAYMENT_METHODS.filter((pm) => {
    if (pm.id === "sumup") return sumupCfg?.appswitch_available !== false;
    return true;
  });
  return (
    <div
      className="md:hidden fixed inset-0 z-50 flex flex-col justify-end animate-fade-in"
      style={{ background: "rgba(0,0,0,0.55)" }}
      data-testid="mobile-checkout-sheet"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-t-[2rem] max-h-[92vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold">
              {activeTeam?.naam}
            </div>
            <div className="font-display text-2xl font-extrabold">Afrekenen</div>
          </div>
          <button
            onClick={onClose}
            className="btn-press w-11 h-11 rounded-full bg-[var(--neutral)] flex items-center justify-center"
            aria-label="Sluiten"
            data-testid="sheet-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-2">
          <div className="divide-y divide-[#ecebe6]">
            {cart.map((x, i) => (
              <div key={x.menu_item_id} className="flex items-center gap-2 py-3" data-testid={`sheet-cart-${x.menu_item_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate text-base">{x.naam}</div>
                  <div className="text-xs text-[var(--ink-soft)]">{fmtEUR(x.prijs)}</div>
                </div>
                <button onClick={() => dec(i)} className="btn-press w-10 h-10 rounded-full bg-white border-2 border-[#ecebe6] flex items-center justify-center" aria-label="Min" data-testid={`sheet-dec-${x.menu_item_id}`}>
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-7 text-center font-display font-bold text-lg">{x.aantal}</span>
                <button onClick={() => inc(i)} className="btn-press w-10 h-10 rounded-full bg-white border-2 border-[#ecebe6] flex items-center justify-center" aria-label="Plus" data-testid={`sheet-inc-${x.menu_item_id}`}>
                  <Plus className="w-4 h-4" />
                </button>
                <button onClick={() => remove(i)} className="btn-press w-10 h-10 rounded-full bg-[var(--danger)] text-white flex items-center justify-center" aria-label="Verwijderen" data-testid={`sheet-rm-${x.menu_item_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <textarea
            value={notitie}
            onChange={(e) => setNotitie(e.target.value)}
            placeholder="Notitie (allergie, extra saus…)"
            className="input min-h-[60px] resize-none mt-3"
            data-testid="sheet-notitie"
          />
          <label className={`flex items-center gap-2 text-sm mt-2 ${bonnenUit ? "hidden" : ""}`}>
            <input
              type="checkbox"
              checked={bonPrinten}
              onChange={(e) => setBonPrinten(e.target.checked)}
              className="w-5 h-5"
              data-testid="sheet-bon-print"
            />
            Bon printen
          </label>
        </div>

        <div className="border-t-2 border-dashed border-[#ecebe6] px-5 pt-3 pb-[max(env(safe-area-inset-bottom),1rem)] bg-white">
          <div className="flex items-baseline justify-between mb-3">
            <span className="text-sm uppercase tracking-widest text-[var(--ink-soft)] font-bold">Totaal</span>
            <span className="font-display text-4xl font-extrabold" data-testid="sheet-totaal">{fmtEUR(totaal)}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {methods.map((pm) => {
              const Icon = pm.icon;
              return (
                <button
                  key={pm.id}
                  onClick={() => onCheckout(pm.id)}
                  disabled={paying || cart.length === 0}
                  className="btn-press flex items-center justify-center gap-2 px-3 py-5 rounded-2xl text-white font-extrabold disabled:opacity-50"
                  style={{ background: pm.color, minHeight: "64px" }}
                  data-testid={`sheet-checkout-${pm.id}`}
                >
                  <Icon className="w-5 h-5" /> {pm.label}
                </button>
              );
            })}
            {sumupCfg?.solo_available && activeTeam?.terminal_id && (
              <button
                onClick={() => onCheckout("sumup-solo")}
                disabled={paying || cart.length === 0}
                className="btn-press col-span-2 flex items-center justify-center gap-2 px-3 py-5 rounded-2xl text-white font-extrabold disabled:opacity-50"
                style={{ background: "#003D7A", minHeight: "64px" }}
                data-testid="sheet-checkout-sumup-solo"
              >
                <Smartphone className="w-5 h-5" /> SumUp Solo Terminal
              </button>
            )}
          </div>
          <button
            onClick={clear}
            className="btn-press w-full mt-2 px-3 py-2 rounded-2xl bg-white border-2 border-[#ecebe6] text-sm font-semibold"
            data-testid="sheet-clear"
          >
            Mandje leegmaken
          </button>
        </div>
      </div>
    </div>
  );
}

function CashChangeModal({ totaal, onConfirm, onCancel }) {
  const [received, setReceived] = useState("");
  const recNum = parseFloat((received || "").replace(",", ".")) || 0;
  const change = recNum - totaal;
  const valid = recNum >= totaal;

  // Snelle bedrag-knoppen — ronde getallen boven het totaal
  const ronde = [];
  const base = Math.ceil(totaal);
  [base, base + 5, base + 10, base + 20, base + 50].forEach((v) => {
    if (v >= totaal && !ronde.includes(v)) ronde.push(v);
  });
  // Volgende €50-grens
  const big50 = Math.ceil(totaal / 50) * 50;
  if (big50 > 0 && !ronde.includes(big50) && ronde.length < 5) ronde.push(big50);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.6)" }}
      data-testid="cash-change-modal"
      role="dialog"
      aria-label="Wisselgeld berekenen"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="bg-white rounded-[2rem] p-6 max-w-md w-full shadow-2xl border-t-[12px] border-[#06D6A0]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-[#06D6A0] flex items-center justify-center">
            <Banknote className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--ink-soft)] font-bold">
              Contant betalen
            </div>
            <div className="font-display text-2xl font-extrabold">Wisselgeld</div>
          </div>
        </div>

        <div className="flex items-baseline justify-between bg-[var(--neutral)] rounded-2xl px-4 py-3 mb-3">
          <span className="text-sm uppercase tracking-widest text-[var(--ink-soft)] font-bold">
            Te betalen
          </span>
          <span className="font-display text-3xl font-extrabold" data-testid="cash-totaal">
            {fmtEUR(totaal)}
          </span>
        </div>

        <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold mb-1">
          Ontvangen van klant
        </label>
        <input
          type="text"
          inputMode="decimal"
          autoFocus
          value={received}
          onChange={(e) => setReceived(e.target.value.replace(/[^0-9.,]/g, ""))}
          placeholder="0,00"
          className="input text-2xl font-display font-extrabold mb-2"
          data-testid="cash-received-input"
        />

        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="cash-quick-buttons">
          {ronde.slice(0, 6).map((v) => (
            <button
              key={v}
              onClick={() => setReceived(v.toFixed(2).replace(".", ","))}
              className="btn-press py-2 rounded-xl bg-white border-2 border-[#ecebe6] font-bold text-sm"
              data-testid={`cash-quick-${v}`}
            >
              €{v}
            </button>
          ))}
        </div>

        <div
          className={`rounded-2xl px-4 py-4 mb-4 text-center ${
            !received
              ? "bg-[var(--neutral)]"
              : valid
              ? "bg-[#E8F8EE] border-2 border-[#06D6A0]"
              : "bg-[#FFE9E0] border-2 border-[var(--danger)]"
          }`}
          data-testid="cash-change-result"
        >
          <div className="text-xs uppercase tracking-widest font-bold text-[var(--ink-soft)]">
            {valid ? "Wisselgeld terug" : received ? "Tekort" : "Vul ontvangen bedrag in"}
          </div>
          <div
            className="font-display text-5xl font-extrabold leading-tight mt-1"
            style={{ color: valid ? "#0E8F2A" : received ? "#D62828" : "#999" }}
            data-testid="cash-change-amount"
          >
            {received ? fmtEUR(Math.abs(change)) : "—"}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="btn-press flex-1 px-4 py-4 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
            data-testid="cash-cancel"
          >
            Annuleer
          </button>
          <button
            onClick={onConfirm}
            disabled={!valid}
            className="btn-press flex-1 px-4 py-4 rounded-2xl bg-[#06D6A0] text-white font-extrabold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cash-confirm"
          >
            Bevestig betaling
          </button>
        </div>
      </div>
    </div>
  );
}



function OrderConfirmModal({ order, onClose, onReshow }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.55)" }}
      data-testid="order-confirm-modal"
      role="dialog"
      aria-label={`Order ${order.ordernummer} bevestigd`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-white rounded-[2rem] p-6 md:p-8 max-w-2xl w-full text-center shadow-2xl"
        style={{ borderTop: `12px solid ${order.team_kleur}` }}
      >
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--ink-soft)] font-bold mb-2">
          {order.team_naam} · Order bevestigd
        </div>
        <div
          className="font-display font-extrabold leading-none my-3 select-all"
          style={{ fontSize: "clamp(6rem, 18vw, 12rem)", color: order.team_kleur }}
          data-testid="confirm-modal-number"
        >
          {order.ordernummer}
        </div>
        <div className="text-2xl md:text-3xl font-display font-bold mt-2">
          {fmtEUR(Number(order.totaal || 0))}
        </div>
        <div className="text-sm text-[var(--ink-soft)] mt-1 capitalize">
          Betaald via {order.betaalmethode}
        </div>
        <div className="mt-3 text-xs text-[var(--ink-soft)] italic">
          Schrijf dit nummer op het afhaalkaartje en geef het aan de klant.
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-6">
          <button
            onClick={onReshow}
            className="btn-press flex-1 px-5 py-4 rounded-2xl bg-[var(--brand-yellow)] text-[var(--ink)] font-extrabold text-lg"
            data-testid="confirm-modal-reshow"
          >
            Toon opnieuw
          </button>
          <button
            onClick={onClose}
            className="btn-press flex-1 px-5 py-4 rounded-2xl text-white font-extrabold text-lg"
            style={{ background: order.team_kleur }}
            data-testid="confirm-modal-close"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}

function printBon(order) {
  const w = window.open("", "_blank", "width=300,height=600");
  if (!w) return;
  const num = (v) => Number(v).toFixed(2).replace(".", ",");
  const lines = order.items
    .map((i) => `<tr><td>${i.aantal}×</td><td>${i.naam}</td><td style="text-align:right">€${num(i.prijs * i.aantal)}</td></tr>`)
    .join("");
  w.document.write(`
    <html><head><title>Bon ${order.ordernummer}</title>
    <style>body{font-family:monospace;padding:12px;width:280px}
    h1{text-align:center;font-size:32px;margin:8px 0;color:${order.team_kleur}}
    table{width:100%;font-size:12px}
    .total{margin-top:8px;border-top:1px dashed #000;padding-top:6px;font-size:16px;display:flex;justify-content:space-between}
    </style></head><body>
    <h1>${order.ordernummer}</h1>
    <div style="text-align:center;font-weight:bold">${order.team_naam}</div>
    <hr/>
    <table>${lines}</table>
    <div class="total"><span>TOTAAL</span><span>€${num(order.totaal)}</span></div>
    <div style="text-align:center;margin-top:8px">${order.betaalmethode}</div>
    ${order.notitie ? `<div style="margin-top:8px;font-style:italic">${order.notitie}</div>` : ""}
    <div style="text-align:center;margin-top:12px;font-size:10px">Bedankt!</div>
    <script>window.print();setTimeout(()=>window.close(),500)</script>
    </body></html>
  `);
}
